import {
  FieldError,
  FieldLabel,
  Modal,
  ModalBody,
  ModalFooter,
  ModalTitle,
  TextField,
} from "@serodigital/ui-components";
import { cloneDeep } from "lodash";
import * as React from "react";
import { useForm } from "react-hook-form";
import { HeatStatus, Zone } from "../../../../shared/api-clients/gql";
import { useHeatingSchedules } from "../../contexts/heatingSchedules";
import {
  generateActiveZones,
  IActiveZones,
  IZoneArray,
} from "../../helpers/scheduleHelper";
import { IZone } from "../../views/bee/state/types";
import { DeleteModal } from "../modal/deleteModal";
import { ScheduleDeleteButton } from "./deleteButton";
import { FormZones } from "./formZones";
import { heatingScheduleValidation } from "./scheduleValidation";
import { Weekday, WeekdayCheckbox } from "./weekdayCheckbox";

interface IScheduleModalProps {
  isOpen: boolean;
  title: string;
  zoneSchedules: IZoneArray;
  zones?: IZone[];
  handleCloseModal: (message?: string) => void;
  selectedSchedule: ISelectedSchedule;
}

interface IDefaultZone extends Omit<Zone, "temperature"> {
  active: boolean;
  temperature: string | number;
}

export interface ISelectedSchedule {
  index: number;
  zoneId: string;
  start: string;
  end: string;
  day: string[];
  temperature: string;
}

export interface IHeatingModalForm {
  zones: IDefaultZone[];
  startTime?: string;
  userTime?: string;
  endTime?: string;
  activeZones?: IActiveZones;
  monday?: boolean;
  tuesday?: boolean;
  wednesday?: boolean;
  thursday?: boolean;
  friday?: boolean;
  saturday?: boolean;
  sunday?: boolean;
}

const reverseLookupHeatStatus = (status?: string) => {
  switch (status) {
    case "OFF":
      return HeatStatus.Off;
    case "ON":
      return HeatStatus.On;
    default:
      return HeatStatus.Unknown;
  }
};

export const HeatingScheduleModal = ({
  isOpen = false,
  title,
  zoneSchedules,
  zones,
  handleCloseModal,
  selectedSchedule,
}: IScheduleModalProps) => {
  const { addMultipleSchedules, editMultipleSchedules } = useHeatingSchedules();
  const [isLoading, setIsLoading] = React.useState(false);
  const [isDeleting, setIsDeleting] = React.useState(false);

  const defaultValuesMemo: IHeatingModalForm =
    React.useMemo<IHeatingModalForm>(() => {
      const startTime = selectedSchedule?.start;
      const endTime = selectedSchedule?.end;
      const activeZones = generateActiveZones(
        zoneSchedules,
        startTime,
        endTime,
        selectedSchedule?.day
      );
      return selectedSchedule
        ? {
            startTime,
            endTime,
            activeZones,
            zones: zones.map((items) => ({
              ...items,
              active: Boolean(activeZones[items.zone_id]),
              temperature: activeZones[items.zone_id]?.temperature ?? 15,
              heating_state: reverseLookupHeatStatus(items.heating_state),
            })),
            ...selectedSchedule.day.reduce(
              (nextItem, prevItem) => ({
                ...nextItem,
                [prevItem]: true,
              }),
              {}
            ),
          }
        : {
            zones: zones?.map((zone) => ({
              temperature: 15,
              active: false,
              description: zone.description,
              lower_bounds: zone.lower_bounds,
              upper_bounds: zone.upper_bounds,
              zone_id: zone.zone_id,
              heating_state: reverseLookupHeatStatus(zone.heating_state),
              setpoint: zone.setpoint,
            })),
            activeZones: {},
          };
    }, []);

  const {
    register,
    handleSubmit,
    control,
    formState: { errors, defaultValues },
  } = useForm<IHeatingModalForm>({
    resolver: heatingScheduleValidation(zoneSchedules, selectedSchedule),
    defaultValues: defaultValuesMemo,
  });

  const onSubmit = async (data) => {
    const newZoneSchedules = cloneDeep(zoneSchedules);
    setIsLoading(true);
    if (selectedSchedule) {
      /** Edit flow */
      const newObj = {
        start: data.startTime,
        end: data.endTime,
        day: data.days,
      };
      for (const zone of data.zones) {
        const zoneId = zone.zone_id;
        const isZoneId = defaultValuesMemo?.activeZones[zoneId];
        let schedules = newZoneSchedules[zoneId];
        if (!schedules) {
          newZoneSchedules[zoneId] = [];
          schedules = newZoneSchedules[zoneId];
        }
        if (zone.active) {
          const { temperature } = zone;
          /** Zone already exists so override index */
          if (isZoneId) {
            schedules[isZoneId.index] = { ...newObj, temperature };
          } else {
            schedules.push({ ...newObj, temperature });
          }
          /** If the zone was active then remove that index */
        } else if (isZoneId) {
          schedules.splice(isZoneId.index, 1);
        }
      }
      await editMultipleSchedules(newZoneSchedules);
    } else {
      await addMultipleSchedules(data);
    }

    setIsLoading(false);
    handleCloseModal();
  };
  async function onDelete() {
    const newZoneSchedules = cloneDeep(zoneSchedules);

    for (const zone of defaultValuesMemo.zones) {
      const zoneId = zone.zone_id;
      const activeZone = defaultValuesMemo?.activeZones[zoneId];
      const schedules = newZoneSchedules[zoneId];
      if (activeZone) {
        schedules.splice(activeZone.index, 1);
      }
    }
    await editMultipleSchedules(newZoneSchedules);
    handleCloseModal("Successfully deleted heating schedule");
  }
  const weekdayError = Boolean((errors as any)?.weekdays); // Fix typing issue

  return (
    <Modal data-qa="schedule-modal" isOpen={isOpen}>
      {isDeleting ? (
        <DeleteModal
          onCancel={() => setIsDeleting(false)}
          onConfirm={onDelete}
          title="Delete Schedule"
          body="Are you sure that you want to delete this schedule?"
          data-qa="schedule-modal"
        />
      ) : (
        <form onSubmit={handleSubmit(onSubmit)}>
          <ModalTitle headerClassName="!ml-0 !mb-8">
            {isDeleting ? "Delete Schedule" : title}
          </ModalTitle>
          <ModalBody>
            <FieldLabel label="Schedule Days" />
            <div className="flex space-x-24">
              <WeekdayCheckbox
                defaultChecked={defaultValues?.monday}
                weekday={Weekday.MONDAY}
                register={register}
                name="monday"
                error={weekdayError}
              />
              <WeekdayCheckbox
                defaultChecked={defaultValues?.tuesday}
                weekday={Weekday.TUESDAY}
                register={register}
                name="tuesday"
                error={weekdayError}
              />
              <WeekdayCheckbox
                defaultChecked={defaultValues?.wednesday}
                weekday={Weekday.WEDNESDAY}
                register={register}
                name="wednesday"
                error={weekdayError}
              />
              <WeekdayCheckbox
                defaultChecked={defaultValues?.thursday}
                weekday={Weekday.THURSDAY}
                register={register}
                name="thursday"
                error={weekdayError}
              />
              <WeekdayCheckbox
                defaultChecked={defaultValues?.friday}
                weekday={Weekday.FRIDAY}
                register={register}
                name="friday"
                error={weekdayError}
              />
              <WeekdayCheckbox
                defaultChecked={defaultValues?.saturday}
                weekday={Weekday.SATURDAY}
                register={register}
                name="saturday"
                error={weekdayError}
              />
              <WeekdayCheckbox
                defaultChecked={defaultValues?.sunday}
                weekday={Weekday.SUNDAY}
                register={register}
                name="sunday"
                error={weekdayError}
              />
            </div>
            <FieldError
              data-qa="weekday-error-field"
              error={weekdayError}
              errorMessage={(errors as any)?.weekdays as unknown as string} // Fix typing issue
            />
            <div className="flex space-x-24 mt-32">
              <TextField
                data-qa="schedule-start-time"
                label="Start time"
                type={"time"}
                {...register("startTime")}
                error={Boolean(errors?.startTime)}
                errorMessage={errors?.startTime as any}
              />
              <TextField
                data-qa="schedule-end-time"
                label="End time"
                type={"time"}
                {...register("endTime")}
                error={Boolean(errors?.endTime)}
                errorMessage={errors?.endTime as any}
              />
            </div>
            <FieldError
              data-qa="form-zones-error-field"
              error={Boolean((errors as any)?.overlap)} // Fix typing issue
              errorMessage={(errors as any)?.overlap as unknown as string} // Fix typing issue
            />
            {zones && (
              <FormZones zones={zones} control={control} register={register} />
            )}
            {selectedSchedule && (
              <ScheduleDeleteButton onClick={() => setIsDeleting(true)} />
            )}
          </ModalBody>
          <ModalFooter
            data-qa="schedule-submit"
            confirmButtonProps={{
              type: "submit",
              className: "!bg-brand-primary !text-common-white",
              disabled: isLoading,
            }}
            confirmLabel={"Save"}
            onCancel={handleCloseModal}
            onConfirm={() => null}
          />
        </form>
      )}
    </Modal>
  );
};
